import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { fmtDate, fmtMoney, fmtNumber } from '../lib/format';
import type { MatchFilter, MatchItem, MatchingResponse } from '../lib/types';
import { Button, Confidence, Empty, ErrorState, Input, Ltr, Pagination, Tabs, useDebounced, useToast } from '../components/ui';
import { ProductPicker } from '../components/ProductPicker';
import { MatchProposals } from '../components/MatchProposals';

const TABS: Array<{ key: MatchFilter; label: string }> = [
  { key: 'pending', label: 'ממתינים לשיוך' },
  { key: 'auto', label: 'שויכו אוטומטית' },
  { key: 'manual', label: 'שויכו ידנית' },
  { key: 'ignored', label: 'לא מוצר מהקטלוג' },
];

export function ProductMatchingPage() {
  const [filter, setFilter] = useState<MatchFilter>('pending');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const dq = useDebounced(q);
  const proposals = useQuery({
    queryKey: ['match-proposals', 'count'],
    queryFn: () => api<{ counts: Record<string, number> }>('/api/products/matching/proposals?confidence=high'),
  });
  const proposalCount = proposals.data ? Object.values(proposals.data.counts).reduce((a, b) => a + b, 0) : 0;
  const [mode, setMode] = useState<'proposals' | 'manual' | null>(null);
  const view = mode ?? (proposalCount > 0 ? 'proposals' : 'manual');
  const { data, error, refetch, isLoading } = useQuery({
    queryKey: ['product-matching', filter, dq, page],
    queryFn: () => api<MatchingResponse>(`/api/products/matching${qs({ filter, q: dq, page, pageSize: 25 })}`),
    placeholderData: (prev) => prev,
  });
  const lc = data?.lineCounts;
  const totalLines = lc ? lc.pending + lc.auto + lc.manual + lc.ignored : 0;
  const pct = (n: number) => (totalLines ? (n / totalLines) * 100 : 0);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumbs"><Link to="/products">קטלוג מוצרים</Link></div>
          <h1>שיוך שורות למוצרים</h1>
          <div className="sub">
            לשורות בהצעות המחיר ובחשבוניות אין מק״ט — רק תיאור. כל תיאור משויך פעם אחת, והשיוך חל על כל השורות עם אותו תיאור, בעבר ובעתיד.
          </div>
        </div>
      </div>

      {data && data.catalogSize === 0 ? (
        <div className="panel">
          <Empty title="הקטלוג עדיין ריק" body="קודם מייבאים את קובץ המוצרים — ואז המערכת משייכת אוטומטית ומציעה התאמות לשאר." actions={<Link to="/products/import" className="btn primary">ייבוא קטלוג</Link>} />
        </div>
      ) : (
        <>
          {lc && totalLines ? (
            <div className="panel panel-body stack" style={{ gap: 8, marginBottom: 12 }}>
              <div className="row wrap" style={{ gap: 14 }}>
                <span className="strong">כיסוי: {Math.round(pct(lc.auto + lc.manual))}% מהשורות משויכות למוצר</span>
                <span className="small muted">
                  {fmtNumber(lc.auto + lc.manual)} מתוך {fmtNumber(totalLines)} שורות · {fmtNumber(lc.pending)} שורות ממתינות · {fmtNumber(lc.ignored)} לא מהקטלוג
                </span>
              </div>
              <div className="coverage" aria-hidden>
                <span style={{ width: `${pct(lc.manual)}%`, background: 'var(--accent)' }} />
                <span style={{ width: `${pct(lc.auto)}%`, background: 'var(--accent-bright)' }} />
                <span style={{ width: `${pct(lc.ignored)}%`, background: 'var(--line-strong)' }} />
              </div>
            </div>
          ) : null}

          {proposalCount > 0 || mode === 'proposals' ? (
            <div className="row" style={{ gap: 8, marginBottom: 12 }}>
              <Button variant={view === 'proposals' ? 'primary' : 'default'} onClick={() => setMode('proposals')}>
                הצעות לאישור מרוכז {proposalCount ? `(${fmtNumber(proposalCount)})` : ''}
              </Button>
              <Button variant={view === 'manual' ? 'primary' : 'default'} onClick={() => setMode('manual')}>שיוך ידני</Button>
            </div>
          ) : null}

          {view === 'proposals' ? <MatchProposals onEmpty={() => setMode('manual')} /> : <>
          <Tabs<MatchFilter>
            value={filter}
            onChange={(k) => {
              setFilter(k);
              setPage(1);
            }}
            tabs={TABS.map((tb) => ({ ...tb, count: data?.counts[tb.key] }))}
          />

          <section className="panel">
            <div className="panel-head row wrap">
              <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="חיפוש בתיאור" style={{ maxWidth: 320 }} aria-label="חיפוש בתיאור" />
              <span className="spacer" />
              {filter === 'pending' ? <span className="small muted">מסודר לפי שכיחות — התיאורים הראשונים מכסים הכי הרבה שורות</span> : null}
            </div>
            {error ? (
              <ErrorState error={error} onRetry={() => refetch()} />
            ) : isLoading || !data ? (
              <div className="panel-body"><span className="spinner" /></div>
            ) : data.items.length === 0 ? (
              <Empty title={filter === 'pending' ? 'אין תיאורים שממתינים לשיוך' : 'אין תיאורים כאן'} body={filter === 'pending' ? 'כל השורות במסמכים משויכות. שורות של מסמכים חדשים ישויכו אוטומטית כשאפשר.' : undefined} />
            ) : (
              <>
                {data.items.map((it) => <MatchRow key={it.key} item={it} filter={filter} />)}
                <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />
              </>
            )}
          </section>
          </>}
        </>
      )}
    </>
  );
}

function MatchRow({ item, filter }: { item: MatchItem; filter: MatchFilter }) {
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  const save = async (productId: number | null | 'reset', label: string) => {
    setBusy(true);
    try {
      await api('/api/products/matching', { body: { key: item.key, description: item.description, productId } });
      toast.success(label);
      for (const k of ['product-matching', 'products', 'price-list']) qc.invalidateQueries({ queryKey: [k] });
    } catch (e) {
      toast.error(e);
      setBusy(false);
    }
  };

  return (
    <div className="match-row" style={busy ? { opacity: 0.5, pointerEvents: 'none' } : undefined}>
      <div style={{ minWidth: 0 }}>
        <div className="strong">{item.description}</div>
        <div className="small muted">
          {fmtNumber(item.lines)} שורות
          {item.lastPrice != null ? ` · אחרון ${fmtMoney(item.lastPrice)}` : ''}
          {item.lastCustomer ? ` ל${item.lastCustomer}` : ''}
          {item.lastDate ? ` · ${fmtDate(item.lastDate)}` : ''}
        </div>
      </div>
      <div style={{ minWidth: 0 }}>
        {item.product && !changing ? (
          <div className="row wrap" style={{ gap: 8 }}>
            <span className="badge ok" style={{ maxWidth: '100%' }}>
              <span className="ellipsis">{item.product.name}</span>
            </span>
            <Ltr className="small muted">{item.product.sku}</Ltr>
            {filter === 'auto' ? <Confidence value={item.confidence} /> : null}
            <span className="spacer" />
            {filter === 'auto' ? <Button size="sm" onClick={() => save(item.product!.id, 'השיוך אושר')}>אישור</Button> : null}
            <Button size="sm" variant="ghost" onClick={() => setChanging(true)}>שינוי</Button>
          </div>
        ) : filter === 'ignored' && !changing ? (
          <div className="row" style={{ gap: 8 }}>
            <span className="small muted">סומן כלא מוצר מהקטלוג (למשל דמי משלוח או שורת הערה)</span>
            <span className="spacer" />
            <Button size="sm" variant="ghost" onClick={() => setChanging(true)}>שיוך למוצר</Button>
          </div>
        ) : (
          <>
            {item.suggestions.length ? (
              <div className="match-sugg">
                {item.suggestions.map((s) => (
                  <Button key={s.productId} size="sm" onClick={() => save(s.productId, `שויך ל"${s.name}"`)} title={`${s.sku} · התאמה ${Math.round(s.score * 100)}%`}>
                    <span className="ellipsis">{s.name}</span>
                    <span className="faint tabular">{Math.round(s.score * 100)}%</span>
                  </Button>
                ))}
              </div>
            ) : null}
            <div className="row" style={{ gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ProductPicker onPick={(p) => save(p.id, `שויך ל"${p.name}"`)} />
              </div>
              {filter !== 'ignored' ? <Button size="sm" variant="ghost" onClick={() => save(null, 'סומן כלא מוצר מהקטלוג')} title="דמי משלוח, הנחה, שורת הערה וכדומה">לא מהקטלוג</Button> : null}
              {changing ? <Button size="sm" variant="ghost" onClick={() => setChanging(false)}>ביטול</Button> : null}
            </div>
            {changing && filter !== 'pending' ? (
              <button type="button" className="link-button small" style={{ marginTop: 6 }} onClick={() => save('reset', 'השיוך אופס')}>איפוס — להחזיר להחלטת המערכת</button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
